/** Two letters for an avatar. "Lokesh Rana" -> "LR", "hr" -> "HR". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** "Wednesday, 30 Sep" in the given timezone. */
export function formatLongDate(instant: Date, tz: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  }).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('weekday')}, ${get('day')} ${get('month')}`;
}

/** "30 Sep 2026, 14:05" in the given timezone. */
export function formatDateTime(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

/** "14:05" in the given timezone. */
export function formatTime(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

/**
 * "9:05 AM", "12:30 PM" in the given timezone: the 12-hour clock, for text people
 * read outside the app (a generated report). Built from `formatTime` so the space
 * is a plain one, not the narrow no-break space Intl puts before "AM".
 */
export function formatTime12(iso: string, tz: string): string {
  const [h, m] = formatTime(iso, tz).split(':').map(Number) as [number, number];
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

const utcDate = (date: string): Date => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
};

/** "Mon 6 Oct" from a 'YYYY-MM-DD' calendar date. */
export function formatShortDate(date: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(utcDate(date));
}

/** "6 Oct" from a 'YYYY-MM-DD' calendar date. */
export function formatDayMonth(date: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
  }).format(utcDate(date));
}

/** "October 2026" from any date in the month. */
export function formatMonthYear(date: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(utcDate(date));
}

/** "5 m ago", "3 h ago", "2 d ago". */
export function relativeTime(iso: string, now = new Date()): string {
  const seconds = Math.round((now.getTime() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

/** "01:24:08" for a running clock. */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

/** "2 h 05 m", "45 m", "0 m" from minutes. */
export function formatMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} m`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} h` : `${h} h ${String(rest).padStart(2, '0')} m`;
}

/** "in 3 days", "today", "2 days ago" from two 'YYYY-MM-DD' dates. */
export function describeDue(date: string, today: string): string {
  const days = Math.round((utcDate(date).getTime() - utcDate(today).getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days > 1) return `in ${days} days`;
  return `${-days} days ago`;
}

/** The 'YYYY-MM-DD' calendar date of an instant in a timezone. */
export function calendarDate(instant: Date, tz: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const WEEKDAY_SHORT = WEEKDAYS;

/** ISO weekday of a 'YYYY-MM-DD' date, 1 = Monday ... 7 = Sunday. */
export function isoWeekdayOf(date: string): number {
  const js = utcDate(date).getUTCDay();
  return js === 0 ? 7 : js;
}

/** "Mon–Sat, 09:30–18:30" or "Mon, Wed, Fri, 10:00–16:00". */
export function describeSchedule(s: { days: number[]; start: string; end: string }): string {
  const days = [...s.days].sort((a, b) => a - b);
  const contiguous = days.every((d, i) => i === 0 || d === days[i - 1]! + 1);
  const label =
    contiguous && days.length > 2
      ? `${WEEKDAYS[days[0]! - 1]}–${WEEKDAYS[days[days.length - 1]! - 1]}`
      : days.map((d) => WEEKDAYS[d - 1]).join(', ');
  return `${label}, ${s.start}–${s.end}`;
}

export const ROLE_LABEL: Record<'owner' | 'manager' | 'sub_manager' | 'member' | 'guest', string> =
  {
    owner: 'Owner',
    manager: 'Manager',
    sub_manager: 'Sub manager',
    member: 'Member',
    guest: 'Guest',
  };

export const STATUS_LABEL: Record<'invited' | 'active' | 'disabled', string> = {
  invited: 'Invited',
  active: 'Active',
  disabled: 'Disabled',
};
